import { useState, useRef, useEffect } from "react";

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  pdfUrl: string;
  pdfFileName: string;
  submitUrl: string;
  formId: string;
  icon: string;
}

export default function LeadFormModal({
  isOpen,
  onClose,
  title,
  subtitle,
  pdfUrl,
  pdfFileName,
  submitUrl,
  formId,
  icon,
}: LeadFormModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setSubmitted(false);
      setError("");
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(submitUrl, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setSubmitted(true);
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = pdfFileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
    >
      <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 relative shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-olive-500 hover:text-olive-700 transition-colors cursor-pointer rounded-full hover:bg-olive-50"
          aria-label="Close modal"
        >
          <i className="fas fa-times text-sm"></i>
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 flex items-center justify-center bg-olive-100 rounded-full text-olive-500 text-2xl mx-auto mb-4">
              <i className="fas fa-check"></i>
            </div>
            <h3 className="font-heading text-xl font-bold text-olive-800 mb-2">
              You're All Set!
            </h3>
            <p className="text-olive-600 text-sm mb-4">
              Your guide is downloading now. Check your inbox for a confirmation
              email too!
            </p>
            <button
              onClick={onClose}
              className="whitespace-nowrap bg-olive-500 hover:bg-olive-600 text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-colors cursor-pointer"
            >
              Got it, thanks!
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-olive-100 rounded-xl text-olive-500 text-xl mb-4">
                <i className={icon}></i>
              </div>
              <h3 className="font-heading text-xl font-bold text-olive-800 mb-2">
                {title}
              </h3>
              <p className="text-olive-600 text-sm">{subtitle}</p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form={formId}
              className="flex flex-col gap-4"
            >
              <div>
                <label
                  htmlFor={`${formId}-name`}
                  className="block text-sm font-medium text-olive-700 mb-1.5"
                >
                  First Name
                </label>
                <input
                  id={`${formId}-name`}
                  name="name"
                  type="text"
                  required
                  placeholder="Your first name"
                  className="w-full px-4 py-3 rounded-xl border border-olive-200 text-sm text-olive-800 placeholder:text-olive-400 focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label
                  htmlFor={`${formId}-email`}
                  className="block text-sm font-medium text-olive-700 mb-1.5"
                >
                  Email Address
                </label>
                <input
                  id={`${formId}-email`}
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-olive-200 text-sm text-olive-800 placeholder:text-olive-400 focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent transition-all"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap w-full bg-olive-500 hover:bg-olive-600 disabled:bg-olive-300 text-white font-semibold py-3 rounded-full text-sm transition-all hover:scale-105 disabled:hover:scale-100 cursor-pointer flex items-center justify-center gap-2 mt-2"
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    Get My Free Guide
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="fas fa-arrow-right text-xs"></i>
                    </span>
                  </>
                )}
              </button>

              <p className="text-olive-400 text-xs text-center">
                No spam, ever. Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}